import { Controller } from '@hotwired/stimulus';
import { useDebounce } from 'stimulus-use';

export default class extends Controller {
  static targets = ['bar', 'scrollLeft', 'scrollRight'];

  static debounces = [{ name: 'persistScrollPosition', wait: 50 }];

  connect() {
    useDebounce(this);

    // Restore scroll locate from previous state, for example, a Turbo drive
    // navigation or page restore from cache.
    const { scrollLeftValue, scrollLeftTargetValue } = this.element.dataset;
    if (scrollLeftValue) {
      this.scrollTo(scrollLeftValue, { behavior: 'instant' });

      if (
        scrollLeftTargetValue != null ||
        scrollLeftTargetValue !== undefined
      ) {
        // If the page has changed while smooth scrolling action still run, we
        // continue it here:
        this.scrollTo(scrollLeftTargetValue, { behavior: 'smooth' });
        delete this.element.dataset.scrollLeftTargetValue;
      }
    }

    // Scroll to first active element, if isn't visible.
    const active = this.barTarget.querySelector('[data-active="true"]');
    this.scrollToIfNeeded(active, { behavior: 'instant' });

    this.updateScrollPosition();
  }

  select(event) {
    this.scrollToIfNeeded(event.target);
  }

  scrollRight() {
    this.scrollTo(this.barTarget.scrollLeft + this.barTarget.offsetWidth / 2);
  }

  scrollLeft() {
    this.scrollTo(this.barTarget.scrollLeft - this.barTarget.offsetWidth / 2);
  }

  turboBeforeRender(event) {
    // Keep the horizontal scroll position when Turbo Drive updates the page:
    //
    // If the page comes from the cache, either when navigating back, or for the
    // Turbo drive preview when navigation to a known page, a data attribute
    // will be present. We will not update it then.
    //
    // When the new pages comes from the server, no data attribute will be
    // present. Then we transfer the current and target position to the new
    // slider. It will use that to restore the scroll position when the Stimulus
    // controller is connected again.
    const newSlider = event.detail.newBody.querySelector(`#${this.element.id}`);
    if (!newSlider) return;

    if (!newSlider.dataset.scrollLeftValue) {
      newSlider.dataset.scrollLeftValue = this.barTarget.scrollLeft;
      if (
        this.scrollLeftTargetValue != null ||
        this.scrollLeftTargetValue !== undefined
      ) {
        newSlider.dataset.scrollLeftTargetValue = this.scrollLeftTargetValue;
      }
    }
  }

  updateScrollPosition() {
    this.persistScrollPosition();

    if (this.scrolledToTheRight()) {
      this.scrollRightTarget.hidden = true;
      this.scrollLeftTarget.hidden = !this.scrollBarNeeded();
    } else if (this.scrolledToTheLeft()) {
      this.scrollRightTarget.hidden = !this.scrollBarNeeded();
      this.scrollLeftTarget.hidden = true;
    } else {
      this.scrollRightTarget.hidden = false;
      this.scrollLeftTarget.hidden = false;
    }
  }

  persistScrollPosition() {
    this.element.dataset.scrollLeftValue = this.barTarget.scrollLeft;
  }

  scrollToIfNeeded(element, opts = {}) {
    // Find the direct child element of the bar: In some cases, for example when
    // `element` comes from `event.target`, it could be a child node inside the
    // button (such as a <span>), and not the direct child element in the bar.
    // Using that element directly will not work since `offsetLeft` and
    // `offsetWidth` would be wrong.
    const button = Array.from(this.barTarget.children).find(
      (child) => child === element || child.contains(element),
    );

    // Some extra clearance to avoid the button hiding below the scroll buttons:
    const pad = 40;
    const bar = this.barTarget;
    if (
      button.offsetLeft - pad < bar.scrollLeft ||
      button.offsetLeft + button.offsetWidth + pad >
        bar.scrollLeft + bar.offsetWidth
    ) {
      const left =
        button.offsetLeft - bar.offsetWidth / 2 + button.offsetWidth / 2;
      this.scrollTo(left, opts);
    }
  }

  scrollTo(left, { behavior = 'smooth' } = {}) {
    this.scrollLeftTargetValue = Math.max(0, left);
    this.barTarget.scrollTo({ left, behavior });
  }

  scrolledToTheRight = () =>
    this.barTarget.scrollLeft >= this.scrollEnd() - 1 &&
    this.barTarget.scrollLeft <= this.scrollEnd() + 1;

  scrolledToTheLeft = () => this.barTarget.scrollLeft === 0;

  scrollBarNeeded = () =>
    this.barTarget.scrollWidth > this.barTarget.offsetWidth;

  scrollEnd = () => this.barTarget.scrollWidth - this.barTarget.offsetWidth;
}
